import React from "react";
import StatWrapper from "../shared/StatWrapper";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import Badge from "hardcover-ui/components/Badge";
import Icon from "hardcover-ui/components/Icon";
import StarIcon from "icons/solid/star.svg";
import BookIcon from "icons/solid/book-blank.svg";
import { useChartTheme } from "../contexts/ChartThemeContext";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "hardcover-ui/components/Tooltip";
import CoverLink from "components/Cover/CoverLink";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import { usePage } from "@inertiajs/react";
import { UserBookSerializersUserBookBylineBook } from "types/serializers";
import Avatar from "hardcover-ui/components/Avatar";
import { scaleLinear } from "d3-scale";
import { range } from "d3-array";
import { curveCatmullRom } from "d3";
import { line } from "d3-shape";

interface MostReadAuthorsStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const RatingGraph: React.FC<{
  userBooks: UserBookSerializersUserBookBylineBook[];
}> = ({ userBooks }) => {
  const { theme } = useChartTheme();
  const height = 140;
  const width = 350;
  const padding = 20;

  if (!userBooks || userBooks.length === 0) {
    return null;
  }

  // D3 Scales
  const xScale = scaleLinear()
    .domain([0, userBooks.length - 1])
    .range([padding, width - padding]);

  const yScale = scaleLinear()
    .domain([1, 5]) // Ratings from 1 to 5
    .range([height - padding, padding]);

  // Create background grid lines for ratings 1-5
  const gridLines = range(1, 6).map((rating) => (
    <g key={rating}>
      <line
        x1={padding}
        y1={yScale(rating)}
        x2={width - padding}
        y2={yScale(rating)}
        stroke="currentColor"
        className="text-gray-200 dark:text-gray-500"
        strokeDasharray="2,6"
      />
      <text
        x={padding - 10}
        y={yScale(rating)}
        className="text-xs fill-gray-200 dark:fill-gray-500"
        textAnchor="end"
        dominantBaseline="middle"
      >
        {rating}
      </text>
    </g>
  ));

  const points = userBooks.map((userBook, index) => ({
    x: xScale(index),
    y: yScale(userBook.rating || 0),
    userBook,
  }));

  const createPath = () => {
    if (points.length < 2) return "";
    const lineGenerator = line<{ x: number; y: number }>()
      .x((d) => d.x as number)
      .y((d) => d.y as number)
      .curve(curveCatmullRom);
    return lineGenerator(points) || "";
  };

  return (
    <div className="relative">
      <svg width={width} height={height} className="ml-auto -mb-6">
        {gridLines}
        <path
          d={createPath()}
          fill="none"
          stroke={
            theme === "colorful"
              ? "var(--chart-colorful-4)"
              : "var(--chart-mono-4)"
          }
          strokeWidth="2"
        />
        {points.map((point, i) => (
          <circle
            key={i}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="white"
            stroke={
              theme === "colorful"
                ? "var(--chart-colorful-4)"
                : "var(--chart-mono-4)"
            }
            strokeWidth="2"
            className="cursor-help"
          />
        ))}
      </svg>

      {/* Tooltips as HTML elements */}
      {points.map((point, i) => (
        <TooltipProvider key={i}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="absolute w-6 h-6 cursor-help"
                style={{
                  left: point.x - 12,
                  top: point.y - 12,
                }}
              />
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                <Text>{point.userBook.book?.title || "Unknown Book"}</Text>
                <div className="flex items-center gap-2">
                  <Text variant="secondary" size="sm">
                    Rating: {point.userBook.rating || "No rating"}
                  </Text>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
};

const MostReadAuthorsStat: React.FC<MostReadAuthorsStatProps> = ({ size }) => {
  const { user, isOwner, stats } = usePage<UserStatsPageProps>().props;
  const { authorsBreakdown: data } = stats;
  const { theme } = useChartTheme();

  if (!data || !data.authors || data.authors.length === 0) {
    return (
      <StatWrapper title="Most Read Authors" size={size}>
        <div className="flex flex-col gap-2 w-full">
          <Heading size="sm" level={3}>
            {isOwner ? "Your" : `${user.name}'s`} author data is not available
          </Heading>
        </div>
      </StatWrapper>
    );
  }

  const topAuthor = data.authors[0];

  return (
    <StatWrapper title="Most Read Authors" size={size}>
      <div className="flex flex-col gap-2 w-full">
        <Heading size="sm" level={3}>
          {isOwner ? "Your" : `${user.name}'s`} most read author is{" "}
          {topAuthor?.author?.name || "Unknown Author"}, with{" "}
          {topAuthor?.booksCount || 0} total reads.
        </Heading>
        {data.authors.map((authorMap) => (
          <div
            key={authorMap.author?.slug || "unknown"}
            className="p-4 rounded-lg bg-gray-100/40 dark:bg-gray-800/40"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4">
                <Avatar
                  image={authorMap.author?.image}
                  alt={authorMap.author?.name || "Unknown Author"}
                  className={`w-24 h-24 rounded-full object-cover ${
                    theme === "monochrome" ? "grayscale" : ""
                  }`}
                />
                <div className="space-y-2">
                  <div className="flex flex-col justify-center gap-2">
                    <Heading size="sm">
                      {authorMap.author?.name || "Unknown Author"}
                    </Heading>
                    <div className="flex items-center flex-wrap gap-2">
                      <Badge
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <Icon icon={BookIcon} size="sm" />{" "}
                        {authorMap.booksCount || 0} books read
                      </Badge>
                      <Badge
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <Icon icon={StarIcon} size="sm" />
                        {authorMap.averageRating ? `${authorMap.averageRating} Avg. Rating` : "No ratings"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-4">
              {authorMap.topRatedBooks && authorMap.topRatedBooks.length > 0 ? (
                <>
                <div className="flex flex-col gap-1">
                  <Text variant="secondary" size="sm">
                    Top Rated Books
                  </Text>
                  <div className="flex gap-4">
                    {authorMap.topRatedBooks?.map((userBook) => (
                      <div key={userBook?.id || "unknown"} className="relative">
                        <CoverLink
                          book={userBook?.book}
                          size="xs"
                          variant="none"
                          settings={{ tooltip: true, details: false }}
                        />
                        <Badge
                          variant="secondary"
                          className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1"
                          size="sm"
                        >
                          <Icon icon={StarIcon} size="xs" />{" "}
                          {userBook?.rating || "No rating"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <RatingGraph userBooks={authorMap.userBooks.filter(userBook => userBook.rating && userBook.rating > 0) || []} />
              </>
              ) : (
                  <Text variant="secondary" size="sm">
                    No books rated
                  </Text>
              )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </StatWrapper>
  );
};

export default MostReadAuthorsStat;
